import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Pagination, Stack, Tooltip, Typography, TextField, IconButton, Button, Skeleton, Alert, CircularProgress } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import { useSite } from "../../context/siteContext";
import { getCategoryCounts } from "../../services/documentService";
import { useAuth } from "../../context/authContext";
import TopProductListItem from "../../components/TopProductListItem/TopProductListItem";
import MachineListItem from "../../components/MachineListItem/MachineListItem";
import PageListItem from "../../components/PageListItem/PageListItem";
import OrderListItem from "../../components/OrderListItem/OrderListItem";
import PageTitle from "../../components/PageTitle/PageTitle";
import KPICard from "../../components/KPICard/KPICard";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import BuildIcon from "@mui/icons-material/Build";
import LinkIcon from "@mui/icons-material/Link";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DescriptionIcon from "@mui/icons-material/Description";
import { useTranslation } from "react-i18next";
import { searchProducts } from "../../services/globalSearchService";
import { getProductsById } from "../../services/cartService"
import DocCard from "../../components/DocCard/DocCard";
import DocumentViewModal from "../../components/DocumentViewModal/DocumentViewModal";
import { Note } from "@mui/icons-material";
import useAccessiblePages from "../../hooks/useAccessiblePages";
import { useTheme } from "@mui/material/styles";
import { canAccessOrderHistory } from "../../utilities/pageAccess";
import { trackEvent } from "../../utilities/analyticsService";

const useQuery = () => new URLSearchParams(useLocation().search);

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const parseNumberArray = (value) => {
  if (Array.isArray(value)) {
    return value.map((val) => Number(val)).filter((num) => Number.isFinite(num));
  }
  if (typeof value === "string") {
    const trimmed = value.replace(/^\{|\}$/g, "");
    if (!trimmed) return [];
    return trimmed
      .split(",")
      .map((val) => Number(val.trim()))
      .filter((num) => Number.isFinite(num));
  }
  return [];
};

const normalizeCategory = (rawCategory) => {
  const c = String(rawCategory || "").trim().toLowerCase();
  if (c === "products" || c === "product") return "Products";
  if (c === "machines" || c === "machine") return "Machines";
  if (c === "orders" || c === "order") return "Orders";
  if (c === "documents" || c === "document") return "Documents";
  if (c === "pages" || c === "page") return "Pages";
  return rawCategory || "";
};

const normalizeSearchResultItem = (item) => {
  const category = normalizeCategory(item?.category);

  if (category === "Documents") {
    const docId = item?.product_id ?? item?.id ?? item?.document_id ?? null;
    const categories = parseJsonArray(item?.categories);
    const machineDetails = parseJsonArray(item?.machine_details);

    const assignedMachines = Array.isArray(item?.assigned_machines)
      ? item.assigned_machines.map((id) => String(id))
      : parseNumberArray(item?.assigned_machines).map((id) => String(id));

    const blobPath = item?.blob_path || item?.attachment || item?.path || null;

    return {
      ...item,
      category: "Documents",
      id: item?.id ?? docId,
      product_id: item?.product_id ?? docId,
      path: item?.path ?? blobPath,
      blob_path: blobPath,
      categories,
      machine_details: machineDetails,
      assigned_machines: assignedMachines,
    };
  }

  if (category) {
    return { ...item, category };
  }

  return item;
};

const getSearchResultKey = (item) => {
  const category = normalizeCategory(item?.category);
  if (category === "Orders") {
    return `Orders:${item?.order_id ?? item?.product_id ?? item?.order_number ?? item?.name ?? ""}`;
  }
  if (category === "Products") {
    return `Products:${item?.product_id ?? ""}:${item?.machine_id ?? ""}`;
  }
  if (category === "Machines") {
    return `Machines:${item?.machine_id ?? item?.product_id ?? item?.machine_number ?? ""}`;
  }
  if (category === "Documents") {
    return `Documents:${item?.id ?? item?.product_id ?? item?.document_id ?? item?.blob_path ?? item?.name ?? ""}`;
  }
  if (category === "Pages") {
    return `Pages:${item?.href ?? item?.name ?? ""}`;
  }
  return `${category || "Unknown"}:${item?.id ?? item?.product_id ?? item?.name ?? JSON.stringify(item)}`;
};

const getProductLineKey = (productId, machineId = null) =>
  `${Number(productId)}:${machineId ? Number(machineId) : ""}`;

const getProductFromMap = (productMap, item = {}) =>
  productMap.get(getProductLineKey(item.product_id, item.machine_id)) ||
  productMap.get(getProductLineKey(item.product_id));

const dedupeSearchResults = (items = []) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = getSearchResultKey(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const pageMatchesSearch = (page, term) => {
  const nameMatch = (page.name || "").toLowerCase().includes(term);
  const searchTermsMatch = (page.searchTerms || []).some((searchTerm) =>
    searchTerm.toLowerCase().includes(term)
  );
  return nameMatch || searchTermsMatch;
};

const getDocSubtitle = (doc, t) => {
  const machines = parseJsonArray(doc?.machine_details)
    .map((machine) => machine?.machine_number || machine?.machine_name)
    .filter(Boolean);
  const categories = parseJsonArray(doc?.categories)
    .map((cat) => cat?.category_name)
    .filter(Boolean);

  const formatList = (values) => {
    const limit = 2;
    if (values.length <= limit) return values.join(", ");
    return `${values.slice(0, limit).join(", ")} +${values.length - limit} more`;
  };

  const machineLabel = t('UI_TEXT.MACHINES') || 'Machines';
  const rawCategoryLabel = t('UI_TEXT.CATEGORIES');
  const categoryLabel = rawCategoryLabel && rawCategoryLabel !== 'UI_TEXT.CATEGORIES' ? rawCategoryLabel : 'Categories';

  const parts = [];
  if (machines.length) parts.push(`${machineLabel}: ${formatList(machines)}`);
  if (categories.length) parts.push(`${categoryLabel}: ${formatList(categories)}`);
  return parts.join(" • ");
};

const getDocChipLabel = (doc) => {
  const type = String(doc?.attachment_type || "").toLowerCase();
  if (type === "url") return "URL";
  if (type === "file") {
    const path = doc?.attachment || doc?.name || "";
    const ext = (path.split(".").pop() || "").toLowerCase();
    if (!ext) return "FILE";
    const map = {
      pdf: "PDF",
      png: "PNG",
      jpg: "JPG",
      jpeg: "JPG",
      csv: "CSV",
      xls: "Excel",
      xlsx: "Excel",
      doc: "Word",
      docx: "Word",
      ppt: "PPT",
      pptx: "PPT",
    };
    return map[ext] || ext.toUpperCase();
  }
  return "DOC";
};

const SearchResults = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const location = useLocation();
  const q = query.get("q") || "";
  const partsOnlyParam = String(query.get("partsonly") || "").toLowerCase();
  const partsOnly =
    partsOnlyParam === "true" ||
    partsOnlyParam === "1" ||
    partsOnlyParam === "yes";
  const { selectedSite, siteData } = useSite();
  const { user, hasPermission, canAddToCart } = useAuth();
  const canShowAddToCart = canAddToCart();
  const accessiblePages = useAccessiblePages();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [products, setProducts] = useState([]);
  const { t } = useTranslation();
  const [search, setSearch] = useState(q);
  const [docModal, setDocModal] = useState({ open: false, doc: null });
  const [activeCategories, setActiveCategories] = useState([]);
  const [documentCategoryCounts, setDocumentCategoryCounts] = useState(null);
  const lastTrackedSearchRef = useRef("");
  // Bumped by "chip:navigate" so a repeat/identical search request (same q, same site) while
  // already on this page retries the fetch instead of leaving a stale error/empty result set
  // in place. Mirrors MyMachines.js's refreshTick pattern.
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    const onChipNavigate = (e) => {
      if (!String(e?.detail?.path || "").startsWith("/search-results")) return;
      setRefreshTick((n) => n + 1);
    };
    window.addEventListener("chip:navigate", onChipNavigate);
    return () => window.removeEventListener("chip:navigate", onChipNavigate);
  }, []);

  const selectedSiteRecord = useMemo(
    () => siteData?.find((site) => site.customer_site_number === selectedSite) || null,
    [siteData, selectedSite]
  );
  const selectedSiteId = selectedSiteRecord?.site_id;

  const pageResults = useMemo(() => {
    if (partsOnly) return [];
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return accessiblePages.filter((page) => pageMatchesSearch(page, s));
  }, [q, accessiblePages, partsOnly]);

  useEffect(() => {
    setSearch(q || "");
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    const loadCounts = async () => {
      // Reset counts immediately so UI doesn't show stale values
      setDocumentCategoryCounts(null);
      if (partsOnly || !q || q.length < 1 || !selectedSiteId) {
        setDocumentCategoryCounts(null);
        return;
      }
      try {
        const resp = await getCategoryCounts(selectedSiteId, { searchTerm: q });
        if (!cancelled) {
          const counts = resp?.data && typeof resp.data === "object" ? resp.data : {};
          setDocumentCategoryCounts(counts);
        }
      } catch (err) {
        console.error("Failed to fetch document category counts:", err);
        if (!cancelled) setDocumentCategoryCounts(null);
      }
    };
    loadCounts();
    return () => {
      cancelled = true;
    };
  }, [q, selectedSiteId, partsOnly]);

  const openDocModal = (doc) => {

    setDocModal({ open: true, doc });
  };

  const closeDocModal = () => {
    setDocModal({ open: false, doc: null });
  };

  useEffect(() => {
    /**
     * Run the search for the current query and hydrate part hits with full product rows.
     *
     * Parts are re-fetched via /products/byids with replacement resolution on, so a superseded
     * part shows its replacement's price and Add to Cart here just like the detail page.
     *
     * @returns {Promise<void>} Updates results/products state; errors clear both lists.
     */
    const run = async () => {
      if (!selectedSite || q.trim().length < 1) {
        setResults([]);
        setProducts([]);
        return;
      }
      // Clear stale results immediately so counts start at 0 while loading new search
      setResults([]);
      setProducts([]);
      setLoading(true);
      try {
        const resp = await searchProducts({
          site_number: selectedSite,
          searchTerm: q,
          ...(user?.user_id ? { user_id: user.user_id } : {}),
        });
        const data = Array.isArray(resp?.data)
          ? dedupeSearchResults(resp.data.map((item) => normalizeSearchResultItem(item)))
          : [];
        const filteredData = partsOnly
          ? data.filter((item) => item.category === "Products" || item.category === "Machines")
          : data;

        // Fetch full product details for Products category
        const productLines = filteredData
          .filter((item) => item.category === "Products")
          .map((item) => ({
            product_id: item.product_id,
            machine_id: item.machine_id,
          }));
        const productIds = Array.from(new Set(productLines.map((item) => item.product_id)));
        let fullProducts = [];
        if (productIds.length > 0) {
          const prodResp = await getProductsById(productIds, selectedSite, productLines, { resolveReplacements: true });
          if (prodResp?.status) {
            fullProducts = prodResp.data;
          }
        }

        setProducts(fullProducts);
        setResults(filteredData);
        const trimmedSearchTerm = q.trim();
        const pageResultCount = partsOnly
          ? 0
          : accessiblePages.filter((page) =>
            pageMatchesSearch(page, trimmedSearchTerm.toLowerCase())
          ).length;

        const resultCount = filteredData.length + pageResultCount;

        const trackingKey = `${trimmedSearchTerm.toLowerCase()}|${selectedSite}|${partsOnly}|${resultCount}`;

        if (trimmedSearchTerm && lastTrackedSearchRef.current !== trackingKey) {
          lastTrackedSearchRef.current = trackingKey;

          trackEvent("search_performed", {
            search_term: trimmedSearchTerm,
            search_source: partsOnly ? "parts_search_results" : "search_results",
            page_name: partsOnly ? "Search Machines & Parts" : "Search Results",
            page_path: `${location.pathname}${location.search || ""}`,
            result_count: resultCount,
            has_results: resultCount > 0,
            no_results: resultCount === 0,
          });
        }
      } catch (e) {
        console.error("Search failed:", e);
        setResults([]);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [q, selectedSite, user?.user_id, partsOnly, accessiblePages, location.pathname, location.search, refreshTick]);

  const triggerSearch = () => {
    if (!search) return;
    const params = new URLSearchParams();
    params.set("q", search);
    if (partsOnly) params.set("partsonly", "true");
    navigate(`/search-results?${params.toString()}`);
  };

  const clearResults = () => {
    setSearch("");
    setResults([]);
    setProducts([]);
    setActiveCategories([]);
    setDocumentCategoryCounts(null);
    window.dispatchEvent(new CustomEvent("global-search:clear"));
    if (partsOnly) {
      navigate(`/search-results?partsonly=true`);
      return;
    }
    navigate(`/search-results`);
  };

  const productMap = useMemo(() => {
    const m = new Map();
    for (const product of products) {
      m.set(getProductLineKey(product.product_id, product.machine_id), product);
      const fallbackKey = getProductLineKey(product.product_id);
      if (!m.has(fallbackKey)) m.set(fallbackKey, product);
    }
    return m;
  }, [products]);

  const documentsList = useMemo(() => results.filter(r => r.category === "Documents"), [results]);

  const documentCategories = useMemo(() => {
    const fallbackName = t('UI_TEXT.UNCATEGORIZED') || 'Uncategorized';
    if (documentCategoryCounts && Object.keys(documentCategoryCounts).length > 0) {
      return Object.entries(documentCategoryCounts)
        .map(([name, count]) => ({
          name: name === "uncategorized" ? fallbackName : name,
          count: Number(count) || 0,
        }))
        .filter((entry) => entry.count > 0)
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    }
    if (!documentsList.length) return [];
    const map = new Map();
    documentsList.forEach((doc) => {
      const categories = Array.isArray(doc.categories) ? doc.categories : [];
      if (!categories.length) {
        const entry = map.get(fallbackName) || { name: fallbackName, count: 0 };
        entry.count += 1;
        map.set(fallbackName, entry);
        return;
      }
      categories.forEach((cat) => {
        const name = (cat?.category_name || fallbackName).trim();
        if (!name) return;
        const entry = map.get(name) || { name, count: 0 };
        entry.count += 1;
        map.set(name, entry);
      });
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [documentCategoryCounts, documentsList, t]);

  const categorized = {
    Machines: results.filter(r => r.category === "Machines"),
    Products: results.filter(r => r.category === "Products" && getProductFromMap(productMap, r)),
    Orders: partsOnly || !canShowAddToCart ? [] : results.filter(r => r.category === "Orders"),
    Pages: partsOnly ? [] : pageResults,
    Documents: partsOnly ? [] : documentsList,
    DocumentCategories: partsOnly ? [] : documentCategories,
  };

  // Pagination state per category
  const pageSize = 5;
  const [machinesPage, setMachinesPage] = useState(1);
  const [productsPage, setProductsPage] = useState(1);
  const [ordersPage, setOrdersPage] = useState(1);
  const [pagesPage, setPagesPage] = useState(1);
  const [documentsPage, setDocumentsPage] = useState(1);
  const [documentCategoriesPage, setDocumentCategoriesPage] = useState(1);

  useEffect(() => {
    // Reset pagination and filters when query or site changes
    setMachinesPage(1);
    setProductsPage(1);
    setOrdersPage(1);
    setPagesPage(1);
    setDocumentsPage(1);
    setDocumentCategoriesPage(1);
    setActiveCategories([]);
  }, [q, selectedSite, partsOnly]);

  const paginate = (arr, page) => arr.slice((page - 1) * pageSize, page * pageSize);
  const machinesPaginated = paginate(categorized.Machines, machinesPage);
  const productsPaginated = paginate(categorized.Products, productsPage);
  const ordersPaginated = paginate(categorized.Orders, ordersPage);
  const pagesPaginated = paginate(categorized.Pages, pagesPage);
  const documentsPaginated = paginate(categorized.Documents, documentsPage);
  const documentCategoriesPaginated = paginate(categorized.DocumentCategories, documentCategoriesPage);

  const sectionConfigs = [
    { key: 'Machines', items: categorized.Machines, itemsPage: machinesPaginated, page: machinesPage, setPage: setMachinesPage },
    { key: 'Products', items: categorized.Products, itemsPage: productsPaginated, page: productsPage, setPage: setProductsPage },
    ...(canShowAddToCart ? [{ key: 'Orders', items: categorized.Orders, itemsPage: ordersPaginated, page: ordersPage, setPage: setOrdersPage }] : []),
    { key: 'Documents', items: categorized.Documents, itemsPage: documentsPaginated, page: documentsPage, setPage: setDocumentsPage },
    { key: 'DocumentCategories', items: categorized.DocumentCategories, itemsPage: documentCategoriesPaginated, page: documentCategoriesPage, setPage: setDocumentCategoriesPage },
    { key: 'Pages', items: categorized.Pages, itemsPage: pagesPaginated, page: pagesPage, setPage: setPagesPage },
  ];
  const nonEmptySections = sectionConfigs
    .filter((sec) => !partsOnly || sec.key === 'Machines' || sec.key === 'Products')
    .filter(sec => sec.items.length > 0);

  const filteredSections = activeCategories.length
    ? nonEmptySections.filter((sec) => activeCategories.includes(sec.key))
    : nonEmptySections;

  // Form rows of two sections each
  const rows = [];
  for (let i = 0; i < filteredSections.length; i += 2) {
    rows.push(filteredSections.slice(i, i + 2));
  }

  const handleCategoryToggle = (key) => {
    setActiveCategories((prev) =>
      prev.includes(key) ? prev.filter((cat) => cat !== key) : [...prev, key]
    );
  };

  const clearCategoryFilters = () => setActiveCategories([]);

  // Navigate to documentation page with the given category pre-selected via URL param
  const handleDocumentCategoryNavigate = (categoryName) => {
    if (!categoryName) return;
    const queryParams = new URLSearchParams();
    queryParams.set('category', categoryName);
    if (q) queryParams.set('q', q);
    navigate(`/documentation?${queryParams.toString()}`, {
      state: {
        highlightCategory: true,
        fromSearchResults: true,
      },
    });
  };

  const docCategoryCountReady = !!documentCategoryCounts;
  const docCategoryCountValue =
    docCategoryCountReady ? documentCategories.length : 0;

  const kpiCards = partsOnly
    ? [
      { key: 'Machines', label: t('UI_TEXT.MACHINES'), value: categorized.Machines.length, color: "#0ea5e9", icon: PrecisionManufacturingIcon },
      { key: 'Products', label: t('UI_TEXT.PARTS'), value: categorized.Products.length, color: "#22c55e", icon: BuildIcon },
    ]
    : [
      { key: 'Machines', label: t('UI_TEXT.MACHINES'), value: categorized.Machines.length, color: "#0ea5e9", icon: PrecisionManufacturingIcon },
      { key: 'Products', label: t('UI_TEXT.PARTS'), value: categorized.Products.length, color: "#22c55e", icon: BuildIcon },
      { key: 'Pages', label: t('UI_TEXT.PAGES'), value: categorized.Pages.length, color: "#a855f7", icon: LinkIcon },
      { key: 'Documents', label: t('UI_TEXT.DOCUMENTS'), value: categorized.Documents.length, color: "#6366f1", icon: DescriptionIcon },
      { key: 'DocumentCategories', label: t('UI_TEXT.DOCUMENT_CATEGORIES'), value: docCategoryCountValue, color: "#ec4899", icon: DescriptionIcon },
      ...(canShowAddToCart ? [{ key: 'Orders', label: t('TITLES.ORDER', { defaultValue: 'Orders' }), value: categorized.Orders.length, color: "#f59e0b", icon: ReceiptLongIcon }] : []),
    ];

  const trimmedQuery = q.trim();
  const partsSearchTitle = t("LABELS.SEARCH_MACHINES_AND_PARTS", {
    defaultValue: "Search Machines & Parts",
  });
  const partsSearchPrompt = t("UI_TEXT.SEARCH_MACHINES_AND_PARTS_PROMPT", {
    defaultValue: "Search by machine number, machine name, part number, or part name.",
  });
  const partsSearchHelper = t("UI_TEXT.SEARCH_MACHINES_AND_PARTS_HELPER", {
    defaultValue: "Only machines and parts are searched for the selected site.",
  });
  const partsSearchPlaceholder = t("PLACEHOLDER.SEARCH_MACHINES_OR_PARTS", {
    defaultValue: "Search machines or parts...",
  });
  const subtitleText = trimmedQuery
    ? partsOnly
      ? `Machine and part results for "${trimmedQuery}"`
      : `Search results for "${trimmedQuery}"`
    : partsOnly
      ? partsSearchPrompt
      : t("UI_TEXT.PLEASE_START_TYPING_TO_SEARCH");
  const subtitle = (
    <span style={{ color: theme.palette.text.secondary }}>{subtitleText}</span>
  );
  const showEmptyPrompt = !loading && trimmedQuery.length < 1;
  const showNoResults = !loading && trimmedQuery.length >= 1 && filteredSections.length === 0;

  return (
    <Box sx={{ p: 2, width: '90vw', mx: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', height: '100%', minHeight: '650px' }}>
      <PageTitle title={partsOnly ? partsSearchTitle : t("UI_TEXT.SEARCH_RESULTS")} subheader={subtitle} />
      <Box sx={{ mb: 2, display: 'flex', columnGap: 1 }}>
        <TextField
          variant="outlined"
          placeholder={partsOnly ? partsSearchPlaceholder : (canShowAddToCart ? t('PLACEHOLDER.SEARCHS', { defaultValue: "Search..." }) : t('PLACEHOLDER.SEARCHS_NO_ORDERS', { defaultValue: "Search..." }))}
          fullWidth
          value={search}
          onChange={(e) => {
            const newValue = e.target.value;
            setSearch(newValue);
            if (newValue.trim() === '' && q) {
              clearResults();
            }
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') triggerSearch(); }}
          size="small"
          slotProps={{
            input: {
              endAdornment: (
                <IconButton onClick={triggerSearch}>
                  <SearchIcon />
                </IconButton>
              ),
            },
          }}
          sx={{
            "& .MuiInputBase-root": { paddingRight: "2px" },
            "& .MuiOutlinedInput-root": { borderRadius: "40px", backgroundColor: "#fff" },
            width: "100%",
          }}
        />
        <Button variant="outlined" onClick={clearResults}>
          {t('BUTTON.CLEAR_RESULTS') || 'Clear'}
        </Button>
      </Box>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        {partsOnly ? partsSearchHelper : t("UI_TEXT.SEARCH_HELPER_TEXT")}
      </Typography>

      {/* KPI Cards */}
      <Stack
        direction="row"
        spacing={2}
        sx={{
          mb: 1,
          py: 1,
          px: 1,
          flexWrap: 'nowrap',
          width: '100%',
          overflowX: 'auto',
        }}
      >
        {kpiCards.map((card) => (
          <KPICard
            key={card.key}
            label={card.label}
            value={card.value}
            color={card.color}
            icon={card.icon}
            active={activeCategories.includes(card.key)}
            onActionClick={() => handleCategoryToggle(card.key)}
          />
        ))}
      </Stack>

      {/* Clear Filters Button */}
      {activeCategories.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="text" onClick={clearCategoryFilters}>
            {t('BUTTON.CLEAR_FILTERS') || 'Clear Filters'}
          </Button>
        </Box>
      )}

      {/* Results */}
      {loading ? (
        <Box sx={{ width: "100%", mt: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
            <CircularProgress size={22} thickness={4.5} />
          </Box>
          <Skeleton variant="text" width="40%" height={32} />
          <Skeleton variant="rectangular" height={120} sx={{ my: 2 }} />
          <Skeleton variant="text" width="60%" height={28} />
          <Skeleton variant="rectangular" height={180} sx={{ my: 2 }} />
        </Box>
      ) : showEmptyPrompt ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {partsOnly ? partsSearchPrompt : t("UI_TEXT.PLEASE_START_TYPING_TO_SEARCH")}
        </Alert>
      ) : showNoResults ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {partsOnly
            ? t("UI_TEXT.NO_MACHINE_OR_PART_RESULTS_FOUND", { defaultValue: "No machine or part results found." })
            : t("UI_TEXT.NO_RESULT_FOUND")}
        </Alert>
      ) : (
        rows.map((row, rowIdx) => (
          <Box key={`row-${rowIdx}`} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', columnGap: 2, alignItems: 'stretch', minWidth: 0 }}>
              {row.map((sec) => (
                // flex-basis 0 + minWidth 0 keeps both columns exactly half the row.
                // Without minWidth:0 a column's automatic minimum size is its content
                // width, so a long, non-wrapping card title widens that column and
                // overflows the page instead of ellipsing inside the card.
                <Box key={`sec-${rowIdx}-${sec.key}`} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, mt: 1, fontWeight: 600 }}>
                    {sec.key === 'Machines' && (t('UI_TEXT.MACHINES') || 'Machines')}
                    {sec.key === 'Products' && (t('UI_TEXT.PARTS') || 'Parts')}
                    {sec.key === 'Pages' && (t('UI_TEXT.PAGES') || 'Pages')}
                    {sec.key === 'Orders' && canShowAddToCart && t('TITLES.ORDER', { defaultValue: 'Orders' })}
                    {sec.key === 'Documents' && (t('UI_TEXT.DOCUMENTS') || 'Documents')}
                    {sec.key === 'DocumentCategories' && (t('UI_TEXT.DOCUMENT_CATEGORIES') || 'Document Categories')}
                  </Typography>

                  {/* Items */}
                  <Box key={`items-${sec.key}-${sec.page}`}>
                    {sec.key === 'Machines' && sec.itemsPage.map((m, idx) => (
                      <MachineListItem key={`mach-${m.machine_id}-${idx}-p${sec.page}`} machine={m} />
                    ))}
                    {sec.key === 'Products' && sec.itemsPage.map((p) => {
                      const full = getProductFromMap(productMap, p);
                      if (!full) return null;
                      return (
                        <TopProductListItem
                          key={`prod-${p.product_id}-${p.machine_id || "none"}-p${sec.page}`}
                          product={{ ...full, machine_number: p.machine_number }}
                          machine_id={p.machine_id}
                          source="search_results"
                        />
                      );
                    })}
                    {sec.key === 'Orders' && canShowAddToCart && sec.itemsPage.map((o, idx) => (
                      <OrderListItem key={`order-${o.product_id}-${idx}-p${sec.page}`} order={o} showTrackButton={false} />
                    ))}
                    {sec.key === 'Pages' && sec.itemsPage.map((p, idx) => (
                      <PageListItem key={`page-${idx}-p${sec.page}`} page={p} />
                    ))}
                    {sec.key === 'Documents' && sec.itemsPage.map((doc) => (
                      <DocCard
                        key={`doc-${doc.product_id}-p${sec.page}`}
                        title={doc.name || 'Document'}
                        subtitle={getDocSubtitle(doc, t)}
                        chipLabel={getDocChipLabel(doc)}
                        imageProps={{
                          path: doc.attachment_type === 'file' ? doc.attachment : undefined,
                          url: doc.attachment_type === 'url' ? doc.attachment : undefined,
                          type: doc.attachment_type,
                        }}
                        rightContent={
                          <Button
                            size="small"
                            variant="text"
                            endIcon={<Note />}
                            onClick={(e) => {
                              e.stopPropagation();
                              let machineObject = null;
                              // Determine the primary category for this document (if any)
                              const categories = Array.isArray(doc.categories) ? doc.categories : [];
                              const primaryCategory = categories.length > 0
                                ? (categories[0]?.category_name || '').trim()
                                : null;

                              if (doc.machine_id) {
                                machineObject = {
                                  machine_id: doc.machine_id,
                                  machine_name: doc.machine_name || '',
                                  machine_number: doc.machine_number || ''
                                };
                              }
                              else if (doc.machine_details && doc.machine_details.length > 0) {
                                const firstMachine = doc.machine_details[0];
                                machineObject = {
                                  machine_id: firstMachine.machine_id,
                                  machine_name: firstMachine.machine_name || '',
                                  machine_number: firstMachine.machine_number || ''
                                };
                              }

                              const queryParams = new URLSearchParams();
                              if (primaryCategory) queryParams.set('category', primaryCategory);
                              if (q) queryParams.set('q', q);
                              const queryPart = queryParams.toString() ? `?${queryParams.toString()}` : '';

                              if (machineObject) {
                                const basePath = `/documentation/${machineObject.machine_id}`;
                                navigate(`${basePath}${queryPart}`, {
                                  state: {
                                    machine: machineObject,
                                    fromSearchResults: true,
                                  },
                                });
                              } else {
                                const basePath = '/documentation';
                                navigate(`${basePath}${queryPart}`, {
                                  state: {
                                    fromSearchResults: true,
                                  },
                                });
                              }
                            }}
                          >
                            {t('UI_TEXT.VIEW_IN_DOCUMENTS') || 'View in Documents'}
                          </Button>
                        }
                        cardProps={{
                          elevation: 0,
                          onClick: () => openDocModal(doc),
                          sx: {
                            width: '100%',
                            borderRadius: 2,
                            border: 'none',
                            backgroundColor: '#fff',
                            boxShadow: '0 0 6px 1px rgba(0,0,0,0.1)',
                            my: 0.75,
                            px: 1,
                            '&:hover': {
                              boxShadow: '0 0 10px 2px rgba(0,0,0,0.2)',
                            },
                            cursor: 'pointer',
                          },
                        }}
                      />

                    ))}

                    {sec.key === 'DocumentCategories' && sec.itemsPage.map((cat) => (
                      <Box
                        key={`doc-cat-${cat.name}-p${sec.page}`}
                        sx={{
                          width: '100%',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          boxShadow: '0 0 6px 1px rgba(0,0,0,0.1)',
                          p: 2,
                          my: 0.75,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          columnGap: 2,
                          '&:hover': {
                            boxShadow: '0 0 10px 2px rgba(0,0,0,0.2)',
                          },
                        }}
                        onClick={() => handleDocumentCategoryNavigate(cat.name)}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Tooltip title={cat.name || ''}>
                            <Typography
                              variant="body1"
                              noWrap
                              sx={{ display: 'block', fontWeight: 600 }}
                            >
                              {cat.name}
                            </Typography>
                          </Tooltip>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {cat.count} {t('UI_TEXT.DOCUMENTS') || 'documents'}
                          </Typography>
                        </Box>
                        <Button size="small" sx={{ flexShrink: 0 }}>
                          {t('UI_TEXT.VIEW_IN_DOCUMENTS') || 'View'}
                        </Button>
                      </Box>
                    ))}
                  </Box>

                  {/* Pagination */}
                  {sec.items.length > pageSize && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                      <Pagination
                        size="small"
                        count={Math.ceil(sec.items.length / pageSize)}
                        page={sec.page}
                        onChange={(_, val) => sec.setPage(val)}
                      />
                    </Box>
                  )}
                </Box>
              ))}
              {row.length === 1 && <Box sx={{ flex: 1, minWidth: 0 }} />}
            </Box>
          </Box>
        ))
      )}

      <DocumentViewModal
        open={docModal.open}
        setOpen={(val) => setDocModal((prev) => ({ ...prev, open: val }))}
        document={docModal.doc}
        onClose={closeDocModal}
      />
    </Box>
  );
};

export default SearchResults;
